<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaAccent, gaiaFontSize, gaiaNeutral, gaiaStatus } from '../themes/tokens';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  /** Vault path under the name; wraps rather than truncates — it identifies the file. */
  path: {
    type: String,
    default: undefined,
  },
  /**
   * ready = imported; processing = still being ingested (no version yet);
   * bad = the import failed, which the row flags with an inline alert glyph.
   */
  status: {
    type: String as PropType<'ready' | 'processing' | 'bad'>,
    default: 'ready',
  },
  /** Version label after the name, e.g. "v3". Dropped while processing. */
  version: {
    type: String,
    default: undefined,
  },
  /** The row the workspace is pointed at — one row, independent of `checked`. */
  selected: {
    type: Boolean,
    default: false,
  },
  /** Bulk-selection state. Set `selectable` to mount the checkbox at all. */
  checked: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{ select: []; 'update:checked': [checked: boolean] }>();

const showVersion = computed(() => props.version && props.status !== 'processing');

const hoverWash = gaiaNeutral.surfaceHover;
const selectedBg = gaiaNeutral.surfaceActive;
const pathColor = gaiaNeutral.textFaint;
const pathSize = gaiaFontSize.nano;
const hintColor = gaiaNeutral.textSubtle;
const alert = gaiaStatus.alert;
const focusRing = gaiaAccent.base;
</script>

<template>
  <!-- One data-room file in the 344px rail (Review Projects mock): the bulk
       checkbox, the name with its failure glyph and version, the vault path,
       and the file's tags underneath.
       Bespoke rather than GaiaPickerOption: that row is a fixed-height single
       line for a picker list, while this one is three stacked lines whose path
       wraps and whose tag row appears only when the file has tags. -->
  <div class="gaia-file-row" :class="{ selected: props.selected }">
    <gaia-checkbox
      v-if="props.selectable"
      :checked="props.checked"
      size="small"
      class="box"
      :aria-label="`Select ${props.name}`"
      @click.stop
      @update:checked="(value: boolean) => emit('update:checked', value)"
    />

    <div class="content">
      <!-- A real button, not a `role="button"` on the row: the row also holds a
           checkbox, editable tag chips and a ⋯ menu, and a button may not
           contain focusable children — a screen reader reaches them but cannot
           say what they are. Opening the file is this button; everything else
           in the row keeps its own tab stop beside it. -->
      <button type="button" class="main" @click="emit('select')">
        <n-flex align="center" :size="6" :wrap="false" :style="{ minWidth: 0 }">
          <span v-if="props.status === 'bad'" class="alert-glyph" title="Import failed">
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            >
              <circle cx="8" cy="8" r="6" />
              <path d="M8 5v3.6" />
              <path d="M8 10.9v.1" />
            </svg>
          </span>
          <gaia-text
            truncate
            size="dense"
            :tone="props.selected ? 'ink' : 'text'"
            :weight="props.selected ? 'semibold' : 'regular'"
            :title="props.name"
            :style="{ minWidth: 0 }"
          >
            {{ props.name }}
          </gaia-text>
          <gaia-text v-if="showVersion" size="nano" tone="muted" :style="{ flex: 'none' }">
            {{ props.version }}
          </gaia-text>
        </n-flex>

        <div v-if="props.path || props.status === 'processing'" class="path">
          <span v-if="props.path" :title="props.path">{{ props.path }}</span>
          <span v-if="props.status === 'processing'" class="hint">Processing…</span>
        </div>
      </button>

      <n-flex v-if="$slots.tags" align="center" :size="5" class="tags">
        <slot name="tags" />
      </n-flex>
    </div>

    <span v-if="$slots.trailing" class="trailing" @click.stop><slot name="trailing" /></span>
  </div>
</template>

<style scoped>
.gaia-file-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 8px;
  border-radius: 8px;
  text-align: left;
}

.gaia-file-row:hover {
  background: v-bind(hoverWash);
}

.gaia-file-row.selected {
  background: v-bind(selectedBg);
}

/* Lines up with the name's first line rather than the row's centre — the row
   is three lines tall and a centred box would float beside the path. */
.box {
  flex: none;
  margin-top: 2px;
}

.content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Stripped back to the block it replaced: the row draws the surface, the hover
   and the cursor, so the button contributes only its semantics and its focus
   ring. `width: 100%` is what keeps the name truncating against the row rather
   than against its own shrink-to-fit width. */
.main {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.main:focus-visible {
  outline: 2px solid v-bind(focusRing);
  outline-offset: 3px;
  border-radius: 4px;
}

.alert-glyph {
  flex: none;
  display: flex;
  color: v-bind(alert);
}

/* Paths have no spaces, so they need an explicit break to wrap at all. */
.path {
  min-width: 0;
  font-size: v-bind(pathSize);
  line-height: 1.45;
  color: v-bind(pathColor);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.hint {
  margin-left: 7px;
  white-space: nowrap;
  color: v-bind(hintColor);
}

.tags {
  min-width: 0;
}

.trailing {
  flex: none;
  margin-top: -1px;
}
</style>
